import 'url-search-params-polyfill';
import MenuPathMap from './MenuPathMap';
import {
  navigationHiddenPartiallyUrls,
  navigationHiddenPartiallyPlusFooterUrls,
  AIPanelHiddenUrls,
  mainWrapperWithoutPaddingUrls,
} from './constants';
import Cookie from 'js-cookie';
import { convertToBillingUrl } from './utils/getBillingBaseUrl';

export const listURLPostfix = `?~(clearFilter~true)`;

/* Convert menuId to navigationState id
 * ex: Zuora/Products => productExpand
 * ex: Zuora/Admin => adminExpand
 * ex: Zuora/MyConnect => myConnectExpand
 */
export const convertMenuIdtoNavigationStateId = (menuId: string) => {
  const [, suffix] = String(menuId).split('/');
  const formatted = suffix.charAt(0).toLowerCase() + suffix.slice(1);
  const formattedPatch = formatted === 'products' ? 'product' : formatted;
  const navStateId = formattedPatch + 'Expand';
  return navStateId;
};

export const getEndpoint = (loc: Location, pathname: string): string => {
  return pathname;
};

export const fetchWrapper = async <T = unknown,>(
  pathname: string,
  init?: RequestInit,
  responseParser?: (res: Response) => Promise<T | Error>,
) => {
  const res = await window.fetch(getEndpoint(window.location, pathname), {
    headers: {
      Accept: 'application/json',
    },
    ...init,
  });

  if (responseParser) {
    const parsedResponse = await responseParser(res);

    // Check if Error was thrown by custom parser
    if (parsedResponse instanceof Error) {
      throw parsedResponse;
    }

    return parsedResponse;
  } else {
    // Needs to be in an "else" block because we never want to parse the body twice (runtime error)

    const body = await res.text();

    // https://react-query.tanstack.com/guides/query-functions#usage-with-fetch-and-other-clients-that-do-not-throw-by-default
    if (!res.ok) {
      throw new Error(body);
    }

    try {
      return JSON.parse(body) as Promise<T>;
    } catch {
      throw new Error(`[${pathname} - ${res.status}]: ${body}`);
    }
  }
};

export const getReplatformedURLfromMenuId = (menuId: string) => {
  return MenuPathMap[menuId];
};

export const getReplatformedURL = async (url: string): Promise<string> => {
  let finalUrl = url;
  try {
    const urlObject = new URL(url);
    if ((window as any).zGetReplatformedUrl) {
      finalUrl = (await (window as any).zGetReplatformedUrl(urlObject)) || url;
    }
  } catch {
    // If url parse or zGetReplatformedUrl fails, use original url
  }
  return convertToBillingUrl(finalUrl);
};

/**
 * Check if current page URL and menu URL have the same query params
 * @param currUrl Current page URL
 * @param menuUrl URL from Navigation Menu API
 */
export const hasMatchingParams = (currUrl: string, menuUrl: string) => {
  // We don't care about most params, so we'll filter out all that are not allowed
  const allowedParams = ['ish'];

  const [currParams, menuParams] = [currUrl, menuUrl].map((url) => {
    const [, search = ''] = url.split('?');
    const params = new URLSearchParams(search);
    const nonAllowedParams: string[] = [];

    /**
     * hack to avoid having to actually use JSURL in this project
     * check for JSURL style param
     */
    if (search.includes('(ish~true)')) {
      params.set('ish', 'true');
    }

    params.forEach((_, key) => {
      if (!allowedParams.includes(key)) {
        nonAllowedParams.push(key);
      }
    });

    nonAllowedParams.forEach((key) => params.delete(key));
    return params;
  });

  return (
    Array.from(menuParams.entries()).every(
      ([key, value]) => currParams.get(key) === value,
    ) &&
    Array.from(currParams.entries()).every(
      ([key, value]) => menuParams.get(key) === value,
    )
  );
};

export const isMenuActiveByMenuUrl = (
  location: Location,
  currUrl = '',
  menuUrl = '',
) => {
  if (!menuUrl) {
    return false;
  } else if (menuUrl === currUrl) {
    return true;
  } else {
    try {
      // const basePath = location.pathname;
      // if (RedirectMap[basePath]) {
      //   const redirectUrl = RedirectMap[basePath]({
      //     location,
      //     queryParams: {},
      //   }).redirectUrl;
      //   return menuUrl.replace(location.origin, '') === redirectUrl;
      // } else {
      /* Deprecation Notice:
          This logic will phase out when all routes are moved onto the RedirectMap
      */
      const [currUrlTokens, menuUrlTokens] = [currUrl, menuUrl].map((url) =>
        url
          .replace(location.origin, '')
          .split('?')[0]
          .split('#')[0]
          .split('/')
          .filter(Boolean),
      );

      return (
        menuUrlTokens.every((t, i) => currUrlTokens[i] === t) &&
        hasMatchingParams(currUrl, menuUrl)
      );
      // }
    } catch {
      return false;
    }
  }
};

export const isMenuActiveByMenuId = (
  location: Location,
  currUrl: string,
  menuId: string,
) => {
  return isMenuActiveByMenuUrl(
    location,
    currUrl,
    getReplatformedURLfromMenuId(menuId),
  );
};

const userId = Cookie.get('Zuora-User-Id');
const currEntityId = Cookie.get('ZuoraCurrentEntity');
export const formatLocalStorageKey = (key: string): string => {
  return `${key}.userId=${userId}&currEntityId=${currEntityId}`;
};

export const isNavigationPartiallyHidden = (currentLocationPath: string) => {
  return !!navigationHiddenPartiallyUrls.find(
    (path) => currentLocationPath.indexOf(path) !== -1,
  );
};

export const isNavigationPartiallyPlusFooterHidden = (
  currentLocationPath: string,
) => {
  return !!navigationHiddenPartiallyPlusFooterUrls.find(
    (path) => currentLocationPath.indexOf(path) !== -1,
  );
};

export const isAIPanelHidden = (currentLocationPath: string) => {
  return !!AIPanelHiddenUrls.find(
    (path) => currentLocationPath.indexOf(path) !== -1,
  );
};

export const isMainWrapperWithoutPadding = (currentLocationPath: string) => {
  return !!mainWrapperWithoutPaddingUrls.find(
    (path) => currentLocationPath.indexOf(path) !== -1,
  );
};
